import { useEffect, useState } from "react";
import api from "@/lib/api";
import { motion } from "framer-motion";
import {
  Network, Building2, Users2, Trophy, Gauge, Loader2, Crown, Medal,
} from "lucide-react";

const STATUS_LABEL = {
  lead: "Lead", contacted: "Contactado", onboarding: "Onboarding", active: "Ativo", churned: "Perdido",
};

function Stat({ icon: Icon, label, value, accent, suffix }) {
  return (
    <div className="p-5 rounded-sm bg-brand-surface border border-brand-border">
      <div className={`h-10 w-10 rounded-sm flex items-center justify-center ${accent}`}><Icon size={18} /></div>
      <p className="font-heading font-black text-3xl mt-3">{value}{suffix}</p>
      <p className="text-sm text-brand-muted">{label}</p>
    </div>
  );
}

export default function MasterOverview() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/master/overview").then((r) => setData(r.data)).catch(() => {}); }, []);

  if (!data) return <div className="h-96 flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-brand-blue" /></div>;

  const rankIcon = (i) =>
    i === 0 ? <Crown size={15} className="text-yellow-400" /> :
    i === 1 ? <Medal size={15} className="text-slate-300" /> :
    i === 2 ? <Medal size={15} className="text-amber-600" /> :
    <span className="text-xs text-brand-muted w-4 text-center">{i + 1}</span>;

  return (
    <div className="space-y-6" data-testid="master-overview">
      <div>
        <span className="text-xs tracking-[0.2em] uppercase font-bold text-brand-orange">Master Franchising</span>
        <h1 className="font-heading font-black text-2xl sm:text-3xl flex items-center gap-2 mt-1">
          <Network className="text-brand-blue" /> {data.network}
        </h1>
        <p className="text-brand-muted text-sm mt-1">Visão agregada da adoção e desempenho da rede.</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat icon={Building2} label="Agências na rede" value={data.agencies_count} accent="bg-brand-blue/15 text-brand-blue" />
        <Stat icon={Users2} label="Membros envolvidos" value={data.consultants_count} accent="bg-brand-orange/15 text-brand-orange" />
        <Stat icon={Trophy} label="Pontos totais" value={data.total_points} accent="bg-brand-success/15 text-brand-success" />
        <Stat icon={Gauge} label="Adoção (onboarding)" value={data.adoption_pct} suffix="%" accent="bg-brand-orange/15 text-brand-orange" />
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Agencies */}
        <div className="p-6 rounded-sm bg-brand-surface border border-brand-border">
          <h3 className="font-heading font-semibold flex items-center gap-2 mb-4"><Building2 size={18} className="text-brand-blue" /> Agências da rede</h3>
          <div className="space-y-2">
            {data.agencies.map((a) => (
              <div key={a.id} data-testid="network-agency-row" className="flex items-center justify-between gap-3 p-3 rounded-sm border border-brand-border">
                <div className="min-w-0">
                  <p className="font-medium truncate">{a.name}</p>
                  <p className="text-xs text-brand-muted">{a.members} membros · {STATUS_LABEL[a.status] || a.status}</p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {a.subscription_status === "active" ? (
                    <span className="text-xs px-2 py-0.5 rounded-sm bg-brand-success/15 text-brand-success font-medium" data-testid="agency-subscription">
                      {a.subscription_plan} · {a.subscription_interval === "annual" ? "Anual" : "Mensal"}
                    </span>
                  ) : (
                    <span className="text-xs px-2 py-0.5 rounded-sm bg-brand-border/60 text-brand-muted" data-testid="agency-subscription">Sem subscrição</span>
                  )}
                  <span className="font-heading font-bold text-brand-orange">{a.points} pts</span>
                </div>
              </div>
            ))}
            {data.agencies.length === 0 && <p className="text-sm text-brand-muted">Sem agências nesta rede.</p>}
          </div>
        </div>

        {/* Top consultants */}
        <div className="p-6 rounded-sm bg-brand-surface border border-brand-border">
          <h3 className="font-heading font-semibold flex items-center gap-2 mb-4"><Trophy size={18} className="text-brand-orange" /> Top consultores da rede</h3>
          <div className="space-y-1">
            {data.top_consultants.map((c, i) => (
              <motion.div key={c.id} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }}
                className="flex items-center gap-3 p-2.5 rounded-sm">
                <div className="w-5 flex justify-center">{rankIcon(i)}</div>
                <div className="h-8 w-8 rounded-sm bg-gradient-to-br from-brand-blue to-brand-orange flex items-center justify-center text-xs font-bold text-white shrink-0">{c.name[0]}</div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium truncate">{c.name}</p>
                  <p className="text-xs text-brand-muted truncate">{c.agency} · {c.level_name}</p>
                </div>
                <span className="text-sm font-bold text-brand-orange">{c.points}</span>
              </motion.div>
            ))}
            {data.top_consultants.length === 0 && <p className="text-sm text-brand-muted">Sem dados.</p>}
          </div>
        </div>
      </div>
    </div>
  );
}
